import {
  CUSTOM_ELEMENTS_SCHEMA,
  DoBootstrap,
  Injector,
  NgModule, Type,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Modules
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { CoreModule } from '@core/core.module';
import { DirectiveModule } from '@shared/directive/directive.module';
import { PrimeNgModule } from '@shared/primeng/prime-ng.module';
import { MncDocumentRoutingModule } from './mnc-document-routing.module';

// Components
import { DocumentsModule } from '@app/documents/documents.module';
import { EmptyComponent } from '@shared/empty/empty.component';
import { MncDocumentComponent } from './mnc-document.component';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { DocumentsWidgetComponent } from '@app/documents/documents-widget/documents-widget.component';
import { API_URL, PLATFORM_API_URL, DOCUMENT_API_URL } from '@core/constants';
import { environment } from '@env/environment';
import { PrimeNGConfigLoader, providePrimeNGFromShell, RouteInterceptor, TokenInterceptor } from '@mynexus/mfe-core';
import { SecurityModule,FeatureToggleModule } from '@mynexus/mynexus-ui-lib';
import { DialogService } from 'primeng/dynamicdialog';
import { DocumentsUploadEmitterComponent } from './documents/documents-upload-emitter/documents-upload-emitter.component';
import { FaxHistoryPanelComponent } from './documents/fax-history/fax-history-panel/fax-history-panel.component';
import { AttachToAuthorizationFormComponent } from './features/components/attach-to-authorization-form/attach-to-authorization-form.component';
import { SampleModule } from './samples/sample.module';

/**
 * MFE-specific default theme loader. Lazy-loads `@mynexus/theme` so the Carelon
 * preset is only fetched when the shell hasn't supplied its own theme.
 */
const loadCarelonPrimeNGConfig: PrimeNGConfigLoader = async () => {
  const { Carelon, CSS_LAYERS_ORDER } = await import('@mynexus/theme');

  return {
    ripple: false,
    theme: {
      preset: Carelon,
      options: {
        darkModeSelector: false,
        cssLayer: {
          name: 'primeng',
          order: CSS_LAYERS_ORDER
        }
      },
    }
  };
};


@NgModule({
  declarations: [
    MncDocumentComponent,
    EmptyComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    PrimeNgModule,
    CoreModule,
    ReactiveFormsModule,
    DirectiveModule,
    DocumentsModule,
    MncDocumentRoutingModule,
    RouterModule,
    SampleModule,
    FeatureToggleModule,
    SecurityModule.forRoot([], environment.apiUrl),
    ...environment.modules,
    FeatureToggleModule.forRoot(`${environment.apiUrl}/api/`),
  ],
  exports: [
    PrimeNgModule,
    DirectiveModule
  ],
  providers: [
    providePrimeNGFromShell(loadCarelonPrimeNGConfig),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RouteInterceptor,
      multi: true
    },
    {
      provide: API_URL,
      useValue: environment.apiUrl,
    },
    {
      provide: PLATFORM_API_URL,
      useValue: environment.platformApiUrl,
    },
    {
      provide: DOCUMENT_API_URL,
      useValue: environment.documentApiUrl,
    },
    DialogService, // PrimeNG's DialogService should be registered at the global level
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MncDocumentModule implements DoBootstrap {
  constructor(private injector: Injector) {
  }

  ngDoBootstrap() {
    this.registerCustomElement<MncDocumentComponent>('mnc-document', MncDocumentComponent);
    this.registerCustomElement<DocumentsWidgetComponent>('mnc-document-widget', DocumentsWidgetComponent);
    this.registerCustomElement<DocumentsUploadEmitterComponent>('mnc-document-upload', DocumentsUploadEmitterComponent);
    this.registerCustomElement<FaxHistoryPanelComponent>('mnc-fax-history', FaxHistoryPanelComponent);
    this.registerCustomElement<AttachToAuthorizationFormComponent>('mnc-attach-to-authorization-form', AttachToAuthorizationFormComponent);

    // need to know when all components are loaded
    const event = new CustomEvent('mnc-document-loaded');
    document.dispatchEvent(event);
  }

  private registerCustomElement<T>(name: string, component: Type<T>) {
    if (customElements.get(name)) return;
    customElements.define(name, createCustomElement<T>(component, { injector: this.injector }));
  }
}
